import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useMe } from "./queries";
import { useMyTeams } from "../lib/queries/teams";

interface Props {
  children: ReactNode;
}

/**
 * Redirects authenticated team leaders to /pending-approval if their
 * only team association is a still-pending team. Admins / members
 * admins are never gated. Falls through when team data has not loaded
 * yet so the rest of the layout can render.
 */
export function PendingTeamGate({ children }: Props) {
  const { data: me } = useMe();
  const { data: teams, isLoading } = useMyTeams();

  if (!me) return <>{children}</>;

  // Global admins bypass the gate.
  const isAdmin = me.assignments.some(
    (a) => (a.role === "admin" || a.role === "members_admin") && a.scope_type === "global",
  );
  if (isAdmin) return <>{children}</>;

  if (isLoading || !teams) return <>{children}</>;
  if (teams.length === 0) return <>{children}</>;

  const allPending = teams.every((t) => t.status === "pending");
  if (allPending) return <Navigate to="/pending-approval" replace />;
  return <>{children}</>;
}
